import React, { useEffect, useMemo, useRef, useState } from 'react';
import { stackAPI, userAPI, twistedStackAPI, groupAPI } from '../services/api';
import { getDiceBearGroupAvatar } from '../services/dicebear';
import { getDiceBearUserAvatar } from '../services/dicebear';
import { getStackDisplayName, getPositionDisplayStyles, getPositionInputStyles, getDynamicFontSize } from '../utils/stack';
import { BUTTON_COLORS } from '../utils/buttonColors';
import { acknowledgeAction } from '../utils/confirmationDialog';
import { TEXT_COLORS } from '../utils/textColors';
import CharacterCounter from '../utils/characterCounter';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { STACK_COLORS, STACK_TEXT_COLORS, getColorIndexForLabel } from '../utils/stackColors';
import { getAvatarStyle } from '../utils/avatar';
import Comments from './Comments';
import Keywords from './Keywords';

// Colors are imported from '../utils/stackColors'

const getScoreText = (score, isCurrentUser) => {
  if (isCurrentUser) {
    return { text: 'me', color: '#6c757d' };
  }
  if (typeof score !== 'number') {
    return null;
  }
  const pctStr = ((score / 75) * 100).toFixed(1) + '%';
  const withPct = (label) => `${label} [${pctStr}]`;
  if (score > 74) return { text: withPct('Besties!'), color: '#28a745' };
  if (score > 70) return { text: withPct('Inner Circle'), color: '#28a745' };
  if (score > 65) return { text: withPct('Vibe Tribe'), color: '#17a2b8' };
  if (score > 59) return { text: withPct('Keeps me Honest'), color: '#007bff' };
  if (score > 52) return { text: withPct('Plus One'), color: '#007bff' };
  if (score > 44) return { text: withPct('Hey Buddy'), color: '#ffc107' };
  if (score > 35) return { text: withPct('Sorta Friends tm'), color: '#fd7e14' };
  if (score > 25) return { text: withPct('Stranger Danger'), color: '#dc3545' };
  if (score > 14) return { text: withPct('Friendemy'), color: '#dc3545' };
  return { text: withPct('Nemesis'), color: '#dc3545' };
};

const SortableCard = ({ card, stack }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: card.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  // Calculate dynamic font size inline to avoid import issues
  const getFontSize = (text, maxWidth = 260, baseSize = 18, minSize = 10) => {
    if (!text || text.length === 0) return baseSize;
    
    // Create a temporary canvas to measure text width accurately
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    
    // Try with base size first
    context.font = `${baseSize}px -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif`;
    const textWidth = context.getTextMetrics ? context.getTextMetrics(text).width : context.measureText(text).width;
    
    // If text fits at base size, no need to reduce
    if (textWidth <= maxWidth) {
      return baseSize;
    }
    
    // Binary search for the optimal font size
    let low = minSize;
    let high = baseSize;
    let optimalSize = minSize;
    
    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      context.font = `${mid}px -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif`;
      const width = context.getTextMetrics ? context.getTextMetrics(text).width : context.measureText(text).width;
      
      if (width <= maxWidth) {
        optimalSize = mid;
        low = mid + 1; // Try a larger size
      } else {
        high = mid - 1; // Try a smaller size
      }
    }
    
    return optimalSize;
  };
  const fontSize = getFontSize(card.label, 260, 18, 10);
  
  // Find the corresponding position object from stack.positions to get the URL
  const positionObj = stack?.positions?.find(p => p?.name === card.label);
  const hasUrl = positionObj?.url;
  
  return (
    <div ref={setNodeRef} style={{ 
      padding: '12px', 
      borderRadius: '8px', 
      backgroundColor: STACK_COLORS[card.id - 1], 
      color: STACK_TEXT_COLORS[card.id - 1], 
      fontWeight: 800, 
      fontSize: `${fontSize}px`, 
      cursor: 'grab', 
      userSelect: 'none', 
      touchAction: 'none', 
      minHeight: '48px', // Consistent height for drag cards
      display: 'flex',
      alignItems: 'center',
      ...style 
    }} {...attributes} {...listeners}>
      <span>{card.label}</span>
      {hasUrl && (
        <span 
          style={{ cursor: 'pointer', fontSize: '16px', marginLeft: '12px' }}
          onClick={(e) => {
            e.stopPropagation(); // Prevent drag when clicking link
            window.open(positionObj.url, '_blank');
          }}
          title={`Open ${positionObj.url}`}
        >
          🔗
        </span>
      )}
    </div>
  );
};

const StackProfile = ({ stackId, onBack, onClose }) => {
  const [stack, setStack] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [order, setOrder] = useState([1, 2, 3, 4, 5, 6]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showHideConfirm, setShowHideConfirm] = useState(false);
  const [hiding, setHiding] = useState(false);
  const [sandboxing, setSandboxing] = useState(false);
  const [showSandboxConfirm, setShowSandboxConfirm] = useState(false);
  const [showTwistDialog, setShowTwistDialog] = useState(false);
  const [twisting, setTwisting] = useState(false);
  const [twistError, setTwistError] = useState('');
  const [twistText, setTwistText] = useState('');
  const twistInputRef = useRef(null);
  const [showEditConfirm, setShowEditConfirm] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', positions: Array(6).fill('') });
  const [editing, setEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showHideForUserDialog, setShowHideForUserDialog] = useState(false);
  const [hidingForUsers, setHidingForUsers] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [groupMembers, setGroupMembers] = useState([]);
  const [selectedStackOrderId, setSelectedStackOrderId] = useState(null);

  const refreshStack = async () => {
    try {
      const s = await stackAPI.getStack(stackId);
      setStack(s.data);
    } catch (err) {
      console.error('Failed to refresh stack:', err);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Help mobile browsers keep focused inputs visible (esp. Firefox mobile)
  const onMobileFocus = (event) => {
    try {
      event.target.scrollIntoView({ block: 'center', behavior: 'smooth' });
      setTimeout(() => {
        try { event.target.scrollIntoView({ block: 'center' }); } catch (_) {}
      }, 250);
    } catch (_) {}
  };

  useEffect(() => {
    if (showTwistDialog) {
      // Focus after modal mounts to ensure keyboard shows and input is visible
      setTimeout(() => {
        try { twistInputRef.current && twistInputRef.current.focus(); } catch (_) {}
      }, 50);
      // Nudge into view shortly after focusing
      setTimeout(() => {
        try { twistInputRef.current && twistInputRef.current.scrollIntoView({ block: 'center' }); } catch (_) {}
      }, 150);
    }
  }, [showTwistDialog]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [me, s] = await Promise.all([
          userAPI.getMe(),
          stackAPI.getStack(`${stackId}?mark_viewed=true`),
        ]);
        setCurrentUser(me.data);
        
        // Check if this is a draft stack
        if (s.data?.is_draft) {
          console.error('Attempted to view draft stack:', s.data.id, '- redirecting to dashboard');
          onBack(); // Redirect to dashboard
          return;
        }
        
        setStack(s.data);
        
        // Set initial selected stack order (preselected one)
        if (s.data.stack_orders && s.data.stack_orders.length > 0) {
          const preselected = s.data.stack_orders.find(so => so.preselected);
          if (preselected) {
            setSelectedStackOrderId(preselected.id);
          } else {
            // Fallback to first one if none are preselected
            setSelectedStackOrderId(s.data.stack_orders[0].id);
          }
        }
      } catch (err) {
        setError('Failed to load stack');
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [stackId, onBack]);

  const needsCompletion = useMemo(() => {
    if (!stack || !currentUser) return false;
    
    // Check if user has already answered
    const hasAnswered = (stack.users || []).some((u) => u.user_id === currentUser.id);
    if (hasAnswered) return false;
    
    // For collaborative stacks, check if user is in the intended audience
    if (stack.is_collab) {
      // Check if user is the owner
      if (currentUser.id === stack.owner_id) return true;
      
      // Check if user is the friend (for friend stacks)
      if (stack.friend_id && currentUser.id === stack.friend_id) return true;
      
      // For group stacks, we need to check if user is a member
      // This is handled by the backend authorization, so if they can view it, they can answer
      if (stack.group_id) return true;
      
      // If none of the above conditions are met, they don't need to complete
      return false;
    }
    
    // For non-collaborative stacks, if they haven't answered, they need to complete
    return !hasAnswered;
  }, [stack, currentUser]);

  const twistEligible = useMemo(() => {
    if (!stack || !currentUser || needsCompletion) return false;
    const answeredUserIds = new Set((stack.users || []).map((u) => u.user_id));

    // Prefer bot-provided audience when present, fallback to stack.audience
    const audience = (stack.bot && stack.bot.audience) ? stack.bot.audience : stack.audience;
    const isFriend = Boolean(audience && audience.user);
    const isGroup = Boolean(audience && audience.group);

    if (isFriend) {
      // Both users must have answered (owner and friend)
      const friendIdLocal = audience.user.id;
      return answeredUserIds.has(currentUser.id) && answeredUserIds.has(friendIdLocal);
    }
    if (isGroup) {
      // Requester answered and at least two other users answered
      if (!answeredUserIds.has(currentUser.id)) return false;
      let others = 0;
      (stack.users || []).forEach((u) => {
        if (u.user_id !== currentUser.id) others += 1;
      });
      return others >= 2;
    }
    return false;
  }, [stack, currentUser, needsCompletion]);

  const sortedCards = useMemo(() => {
    if (!stack) return [];
    return order.map((posId) => {
      const position = stack.positions[posId - 1];
      const label = typeof position === 'string' ? position : position?.name || '';
      return { id: posId, label };
    });
  }, [stack, order]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setOrder((items) => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleSubmit = async () => {
    // Prevent double-clicks by checking if already submitting
    if (submitting) return;
    
    try {
      setSubmitting(true);
      await stackAPI.completeStack(stackId, order, selectedStackOrderId);
      const refreshed = await stackAPI.getStack(stackId);
      setStack(refreshed.data);
    } catch (err) {
      setError('Failed to submit');
      console.error(err);
      setSubmitting(false);
    }
    // Note: Don't setSubmitting(false) on success to keep button disabled
  };

  const handleHideStack = async () => {
    try {
      setHiding(true);
      await stackAPI.hideStack(stackId);
      setShowHideConfirm(false);
      onBack();
    } catch (err) {
      setError('Failed to hide stack');
      console.error(err);
    } finally {
      setHiding(false);
    }
  };

  const handleDeleteStack = async () => {
    try {
      setDeleting(true);
      await stackAPI.deleteStack(stackId);
      setShowDeleteConfirm(false);
      onBack();
    } catch (err) {
      setError('Failed to delete stack');
      console.error(err);
    } finally {
      setDeleting(false);
    }
  };

  const toggleSandbox = async () => {
    try {
      if (!stack) return;
      setSandboxing(true);
      const next = !Boolean(stack.is_sandboxed);
      await stackAPI.setSandbox(stackId, next);
      const refreshed = await stackAPI.getStack(stackId);
      setStack(refreshed.data);
      setShowSandboxConfirm(false);
    } catch (err) {
      setError('Failed to update sandbox state');
      console.error(err);
    } finally {
      setSandboxing(false);
    }
  };

  const handleCreateTwist = async () => {
    if (!stack) return;
    try {
      setTwisting(true);
      setTwistError('');
      const desc = (twistText || '').trim();
      if (!desc) {
        setTwistError('Please enter a twist (max 48 characters).');
        return;
      }
      const created = await twistedStackAPI.createTwistedStack({ stack_id: Number(stack.id), twist_description: desc });
      setShowTwistDialog(false);
      setTwistText('');
      // Open the newly created twisted stack directly
      try { window.dispatchEvent(new CustomEvent('openStack', { detail: { id: created?.data?.id, type: 'twisted' } })); } catch (_) {}
    } catch (err) {
      const toMessage = (value) => {
        if (!value) return '';
        if (typeof value === 'string') return value;
        if (Array.isArray(value)) {
          // FastAPI/Pydantic style: [{loc, msg, type}, ...] or array of strings
          const parts = value.map((v) => {
            if (typeof v === 'string') return v;
            if (v && typeof v === 'object') {
              if (v.msg) return v.msg;
              if (v.message) return v.message;
              if (v.detail) return toMessage(v.detail);
            }
            try { return JSON.stringify(v); } catch { return String(v); }
          });
          // Try to surface missing field names if present
          const missing = value
            .filter((v) => v && typeof v === 'object' && v.msg === 'Field required' && Array.isArray(v.loc))
            .map((v) => v.loc[v.loc.length - 1])
            .filter(Boolean);
          if (missing.length > 0) {
            const filtered = missing.filter((m) => m !== 'request' && m !== 'payload');
            if (filtered.length > 0) {
              return `Missing required field${filtered.length > 1 ? 's' : ''}: ${filtered.join(', ')}`;
            }
            // Only framework-level fields missing → likely empty or malformed body
            return 'Request body missing or malformed. Please try again.';
          }
          return parts.filter(Boolean).join('; ');
        }
        if (typeof value === 'object') {
          if (value.msg) return value.msg;
          if (value.message) return value.message;
          if (value.detail) return toMessage(value.detail);
          // Fall back to compact JSON
          try { return JSON.stringify(value); } catch { return String(value); }
        }
        return String(value);
      };

      const raw = err?.response?.data?.detail ?? err?.response?.data ?? err?.message ?? err;
      const message = toMessage(raw) || 'Failed to create twist';
      setTwistError(message);
    } finally {
      setTwisting(false);
    }
  };

  const handleEditStack = async () => {
    if (!stack) return;
    try {
      setEditing(true);
      await stackAPI.editStack(stackId, {
        name: editForm.name || null,
        positions: editForm.positions
      });
      setShowEditDialog(false);
      // Reload the stack to get updated data
      const refreshed = await stackAPI.getStack(stackId);
      setStack(refreshed.data);
    } catch (err) {
      console.error('Failed to edit stack:', err);
      if (err.response?.status === 403) {
        // Stack can no longer be edited (time expired or submissions added)
        await acknowledgeAction(
          'Cannot Edit Stack',
          'This stack can no longer be edited. It may have been created more than an hour ago or someone has already responded.'
        );
        setShowEditDialog(false);
        // Reload the stack to get updated data and hide the edit button
        const refreshed = await stackAPI.getStack(stackId);
        setStack(refreshed.data);
      } else {
        // Other errors
        await acknowledgeAction(
          'Error',
          'Failed to edit stack. Please try again.'
        );
      }
    } finally {
      setEditing(false);
    }
  };

  const handleEditClick = () => {
    // Pre-populate the edit form with current stack data
    setEditForm({
      name: stack.name || '',
      positions: Array.isArray(stack.positions) 
        ? stack.positions.map(p => typeof p === 'string' ? p : (p?.name || ''))
        : Array(6).fill('')
    });
    setShowEditConfirm(true);
  };

  const handleHideForUserClick = async () => {
    if (!stack?.group_id) return;
    
    try {
      // Fetch group members
      const groupData = await groupAPI.getGroup(stack.group_id);
      const members = groupData.data.members || [];
      
      // Filter out users who have already answered the stack
      const answeredUserIds = new Set((stack.users || []).map(u => u.user_id));
      const availableMembers = members.filter(member => 
        !answeredUserIds.has(member.user_id) && member.user_id !== currentUser?.id
      );
      
      setGroupMembers(availableMembers);
      setSelectedUserIds([]);
      setShowHideForUserDialog(true);
    } catch (err) {
      console.error('Failed to load group members:', err);
      setError('Failed to load group members');
    }
  };

  const handleHideForUsers = async () => {
    if (selectedUserIds.length === 0) return;
    
    try {
      setHidingForUsers(true);
      await stackAPI.hideStackForUsers(stackId, selectedUserIds);
      setShowHideForUserDialog(false);
      setSelectedUserIds([]);
      // Refresh the stack to update the UI
      await refreshStack();
    } catch (err) {
      console.error('Failed to hide stack for users:', err);
      setError('Failed to hide stack for users');
    } finally {
      setHidingForUsers(false);
    }
  };

  if (loading) return <div>Loading stack...</div>;
  if (error) return <div style={{ color: 'red' }}>{error}</div>;
  if (!stack) return null;

  // Build owner avatar URL for header
  const ownerAvatarUrl = getDiceBearUserAvatar(stack.owner_username, stack.owner_avatar_id ?? 1);
  const ownerId = typeof stack.owner_id === 'number' ? stack.owner_id : null;
  const groupName = stack?.audience?.group ? stack.audience.group.name : (stack?.bot?.audience?.group ? stack.bot.audience.group.name : null);
  const groupId = stack?.audience?.group ? stack.audience.group.id : (stack?.bot?.audience?.group ? stack.bot.audience.group.id : null);
  const groupAvatarUrl = groupName ? getDiceBearGroupAvatar(groupName) : null;
  const visibleResponsesCount = (stack.users || []).filter(u => !(stack.blocked_user_ids || []).includes(u.user_id)).length;
  const friendId = stack?.audience?.user ? stack.audience.user.id : (stack?.bot?.audience?.user ? stack.bot.audience.user.id : null);
  const friendName = stack?.audience?.user ? stack.audience.user.username : (stack?.bot?.audience?.user ? stack.bot.audience.user.username : null);
  let friendAvatarId = null;
  if (friendId) {
    const foundUser = (stack.users || []).find(u => u.user_id === friendId);
    if (foundUser && typeof foundUser.avatar_id === 'number') {
      friendAvatarId = foundUser.avatar_id;
    } else if (stack.owner_id === friendId && typeof stack.owner_avatar_id === 'number') {
      friendAvatarId = stack.owner_avatar_id;
    }
  }
      const friendAvatarUrl = friendName ? getDiceBearUserAvatar(friendName, friendAvatarId ?? 1) : null;


  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <h2 style={{ 
          margin: 0, 
          paddingRight: '20px', 
          fontSize: `${getDynamicFontSize(getStackDisplayName(stack), 400, 24, 8)}px`,
          lineHeight: '1.2',
          whiteSpace: 'nowrap'
        }}>{getStackDisplayName(stack)}</h2>
        <button onClick={onClose} className="close-btn">&times;</button>
      </div>

      {/* Collaboration label for collaborative stacks */}
      {stack?.is_collab && (
        <div style={{ marginTop: '-6px', marginBottom: '12px' }}>
          <span style={{ color: TEXT_COLORS.BANNER_COLLAB, fontWeight: 800, fontStyle: 'italic' }}>Collaboration</span>
        </div>
      )}

      {/* Header meta: Created by avatar link */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '6px 0 18px 0', color: 'var(--color-text)' }}>
        <span style={{ fontWeight: 600 }}>Created by:</span>
        {stack.bot ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <button
              type="button"
              title={stack.bot.name}
              onClick={() => {
                try { window.dispatchEvent(new CustomEvent('openBot', { detail: { botId: stack.bot.id } })); } catch (_) {}
              }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}
            >
              <img src={`/assets/${stack.bot.avatar_id}`} alt={`${stack.bot.name} avatar`} style={getAvatarStyle('medium')} />
              <span style={{ color: 'var(--color-primary)', textDecoration: 'underline' }}>{stack.bot.name}</span>
            </button>
          </div>
        ) : (
          <button
            type="button"
            title={stack.owner_username}
            onClick={() => {
              if (ownerId) {
                try { window.dispatchEvent(new CustomEvent('openUser', { detail: { userId: ownerId } })); } catch (_) {}
              }
            }}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}
          >
            <img src={ownerAvatarUrl} alt={`${stack.owner_username} avatar`} style={getAvatarStyle('medium')} />
            <span style={{ color: 'var(--color-primary)', textDecoration: 'underline' }}>{stack.owner_username}</span>
          </button>
        )}
      </div>

      {/* Generated by section (only for USER_DRIVEN bots), placed between Created by and Keywords */}
      {stack.bot && stack.bot.type === 'USER_DRIVEN' && stack.bot.executor && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '-8px 0 18px 0', color: 'var(--color-text)' }}>
          <span style={{ fontWeight: 600 }}>Generated by:</span>
          <button
            type="button"
            title={stack.bot.executor.username}
            onClick={() => {
              try { window.dispatchEvent(new CustomEvent('openUser', { detail: { userId: stack.bot.executor.id } })); } catch (_) {}
            }}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}
          >
            <img 
              src={getDiceBearUserAvatar(stack.bot.executor.username, stack.bot.executor.avatar_id)} 
              alt={`${stack.bot.executor.username} avatar`} 
              style={getAvatarStyle('medium')} 
            />
            <span style={{ color: 'var(--color-primary)', textDecoration: 'underline' }}>{stack.bot.executor.username}</span>
          </button>
        </div>
      )}

      {/* Keywords section below Created by */}
      {stack.bot && Array.isArray(stack.bot.keywords) && stack.bot.keywords.length > 0 && (
        <Keywords keywords={stack.bot.keywords} label="Keywords:" />
      )}

      {/* Collaborators list for collaborative stacks */}
      {stack?.is_collab && Array.isArray(stack.collab) && stack.collab.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '-8px 0 18px 0', color: 'var(--color-text)' }}>
          <span style={{ fontWeight: 600 }}>Collaborators:</span>
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
            {stack.collab.map((c) => (
              <button
                key={`collab-${c.id}`}
                type="button"
                title={c.username}
                onClick={() => {
                  try { window.dispatchEvent(new CustomEvent('openUser', { detail: { userId: c.id } })); } catch (_) {}
                }}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'transparent', border: '1px solid var(--color-border)', borderRadius: '999px', padding: '4px 8px', cursor: 'pointer' }}
              >
                <img src={getDiceBearUserAvatar(c.username, c.avatar_id ?? 1)} alt={`${c.username} avatar`} style={{ width: '22px', height: '22px', borderRadius: '50%' }} />
                <span style={{ color: 'var(--color-primary)' }}>{c.username}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {groupName && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '-8px 0 18px 0', color: 'var(--color-text)' }}>
          <span style={{ fontWeight: 600 }}>Group:</span>
          <button
            type="button"
            title={groupName}
            onClick={() => {
              if (groupId) {
                try { window.dispatchEvent(new CustomEvent('openGroup', { detail: { groupId } })); } catch (_) {}
              }
            }}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}
          >
            <img src={groupAvatarUrl} alt="group avatar" style={getAvatarStyle('medium')} />
            <span style={{ color: 'var(--color-primary)', textDecoration: 'underline', fontWeight: 600 }}>{groupName}</span>
          </button>
        </div>
      )}

      {friendName && friendId && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '-8px 0 18px 0', color: 'var(--color-text)' }}>
          <span style={{ fontWeight: 600 }}>Friend:</span>
          <button type="button" title={friendName} onClick={() => { try { window.dispatchEvent(new CustomEvent('openUser', { detail: { userId: friendId } })); } catch (_) {} }} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}>
            <img src={friendAvatarUrl} alt={`${friendName} avatar`} style={getAvatarStyle('medium')} />
            <span style={{ color: 'var(--color-primary)', textDecoration: 'underline' }}>{friendName}</span>
          </button>
        </div>
      )}

      {/* Group Consensus "response" card at top of responses grid */}

      {needsCompletion ? (
        <div>
          {/* Rank By dropdown section */}
          {stack?.stack_orders && stack.stack_orders.length > 0 && (
            <div style={{ marginBottom: '20px', padding: '16px', backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <label style={{ fontWeight: '600', color: 'var(--color-text)', fontSize: '16px' }}>
                  Rank By:
                </label>
                <select
                  value={selectedStackOrderId || ''}
                  onChange={(e) => setSelectedStackOrderId(parseInt(e.target.value))}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid var(--color-border)',
                    borderRadius: '4px',
                    backgroundColor: 'var(--color-background)',
                    color: 'var(--color-text)',
                    fontSize: '14px',
                    minWidth: '200px'
                  }}
                >
                  {stack.stack_orders.map((stackOrder) => (
                    <option key={stackOrder.id} value={stackOrder.id}>
                      {stackOrder.name}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ fontSize: '14px', color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>
                Choose how you want to rank the items
              </div>
            </div>
          )}
          
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={sortedCards.map(card => card.id)} strategy={verticalListSortingStrategy}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '10px', margin: '20px 0' }}>
                {sortedCards.map((card) => (
                  <SortableCard key={card.id} card={card} stack={stack} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
          <button className="btn" style={{ backgroundColor: '#28a745' }} disabled={submitting} onClick={handleSubmit}>
            {submitting ? 'Submitting...' : 'Submit'}
          </button>

          {/* Stack Actions for incomplete stacks */}
          <div style={{ marginTop: '20px' }}>
            <h3>Stack Actions</h3>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '10px' }}>
              <button onClick={onBack} className="btn" style={{ backgroundColor: 'var(--color-primary)', color: 'var(--text-on-primary)' }}>
                Back
              </button>
              {/* Show Edit button only if user is owner, no submissions, and within 1 hour */}
              {currentUser?.id === ownerId && (!stack.users || stack.users.length === 0) && (() => {
                const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
                const createdAt = new Date(stack.created_at);
                return createdAt > oneHourAgo;
              })() && (
                <button onClick={handleEditClick} className="btn" style={{ backgroundColor: '#28a745', color: 'white' }}>
                  Edit
                </button>
              )}
              <button onClick={() => setShowHideConfirm(true)} className="btn" style={{ backgroundColor: 'var(--color-warning)', color: 'var(--text-on-warning)' }}>
                Hide
              </button>
              {/* Hide for User button - admin only, group stacks only */}
              {currentUser?.role === 'ADMIN' && Boolean(groupId) && (
                <button onClick={handleHideForUserClick} className="btn" style={{ backgroundColor: BUTTON_COLORS.MODERATE_BATCH, color: 'white' }}>
                  Hide for User
                </button>
              )}
              {Boolean(groupId) && (
                (currentUser?.role === 'ADMIN') || (!stack?.is_sandboxed && currentUser?.id === ownerId)
              ) && (
                <button onClick={() => setShowSandboxConfirm(true)} className="btn" style={{ backgroundColor: stack?.is_sandboxed ? '#6c757d' : '#fd7e14', color: 'white' }} disabled={sandboxing}>
                  {sandboxing ? (stack?.is_sandboxed ? 'Unsandboxing...' : 'Sandboxing...') : (stack?.is_sandboxed ? 'Unsandbox' : 'Sandbox')}
                </button>
              )}
              {/* Delete button for bot-generated stacks (admin only) */}
              {stack?.bot && currentUser?.role === 'ADMIN' && (!stack?.users || stack.users.length === 0) && (
                <button onClick={() => setShowDeleteConfirm(true)} className="btn" style={{ backgroundColor: BUTTON_COLORS.DELETE, color: 'white' }}>
                  Delete
                </button>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div>
          <h3>Responses</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
            {stack.group_id && Array.isArray(stack.consensus) && stack.consensus.length > 0 && (
              <div style={{ border: '1px solid var(--color-border)', background: 'var(--color-surface)', color: 'var(--color-text)', borderRadius: '8px', padding: '12px', minWidth: '280px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <img src={getDiceBearGroupAvatar(stack.audience?.name || 'Group')} alt="group avatar" style={{ width: '28px', height: '28px', borderRadius: '50%' }} />
                    <div style={{ fontWeight: 800 }}>Group Consensus ({visibleResponsesCount} responses)</div>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '8px' }}>
                  {stack.consensus.map((label, idx) => {
                    const colorIndex = getColorIndexForLabel(stack.positions, label, idx);
                    const contributor = Array.isArray(stack.collab)
                      ? stack.collab.find((c) => Array.isArray(c.positions) && c.positions.includes(idx + 1))
                      : null;
                    
                    return (
                      <div key={`consensus-inline-${idx}`} style={{ position: 'relative' }}>
                        <div style={getPositionDisplayStyles(label, STACK_COLORS[colorIndex], STACK_TEXT_COLORS[colorIndex])}>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <span>{label}</span>
                            {(() => {
                              // Find the corresponding position object from stack.positions to get the URL
                              const positionObj = stack.positions?.find(p => p?.name === label);
                              return positionObj?.url ? (
                                <span 
                                  style={{ cursor: 'pointer', fontSize: '16px', marginLeft: '12px' }}
                                  onClick={() => window.open(positionObj.url, '_blank')}
                                  title={`Open ${positionObj.url}`}
                                >
                                  🔗
                                </span>
                              ) : null;
                            })()}
                          </div>
                        </div>
                        {contributor && stack.is_collab && Array.isArray(stack.collab) && (
                          <img
                            src={getDiceBearUserAvatar(contributor.username, contributor.avatar_id ?? 1)}
                            alt={contributor.username}
                            title={contributor.username}
                            style={{ position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)', width: '20px', height: '20px', borderRadius: '50%' }}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {(stack.users || [])
              .filter(u => !(stack.blocked_user_ids || []).includes(u.user_id))
              .sort((a, b) => (a.user_id === currentUser?.id ? -1 : b.user_id === currentUser?.id ? 1 : 0))
              .map((u) => (
              <div key={u.id} style={{ border: '1px solid var(--color-border)', background: 'var(--color-surface)', color: 'var(--color-text)', borderRadius: '8px', padding: '12px', minWidth: '280px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <img src={getDiceBearUserAvatar(u.username, u.avatar_id)} alt="avatar" style={{ width: '28px', height: '28px', borderRadius: '50%' }} />
                    <div style={{ fontWeight: 700 }}>{u.username}</div>
                  </div>
                  {(() => {
                    const scoreInfo = getScoreText(u.score, u.user_id === currentUser?.id);
                    return scoreInfo ? (
                      <div style={{ color: scoreInfo.color, fontWeight: 600, fontSize: '14px' }}>
                        {scoreInfo.text}
                      </div>
                    ) : null;
                  })()}
                </div>
                {u.stack_order_name && (
                  <div style={{ marginBottom: '8px', fontSize: '14px', color: 'var(--color-text-secondary)', fontStyle: 'italic', fontWeight: 'bold' }}>
                    Ranked by {u.stack_order_name}
                  </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '8px' }}>
                  {u.positions.map((label, idx) => {
                    const colorIndex = getColorIndexForLabel(stack.positions, label, idx);
                    return (
                      <div key={`${u.id}-${idx}`} style={getPositionDisplayStyles(label, STACK_COLORS[colorIndex], STACK_TEXT_COLORS[colorIndex])}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <span>{label}</span>
                          {(() => {
                            // Find the corresponding position object from stack.positions to get the URL
                            const positionObj = stack.positions?.find(p => p?.name === label);
                            return positionObj?.url ? (
                              <span 
                                style={{ cursor: 'pointer', fontSize: '16px', marginLeft: '12px' }}
                                onClick={() => window.open(positionObj.url, '_blank')}
                                title={`Open ${positionObj.url}`}
                              >
                                🔗
                              </span>
                            ) : null;
                          })()}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Comments section - only show for completed stacks */}
          {stack.comments && (
            <Comments
              comments={stack.comments}
              currentUser={currentUser}
              stackId={stackId}
              isTwisted={false}
              onCommentUpdate={refreshStack}
              canSandbox={Boolean(groupId) && (
                (currentUser?.role === 'ADMIN') || (currentUser?.id === ownerId)
              )}
            />
          )}

          {/* Stack Actions for completed stacks */}
          <div style={{ marginTop: '20px' }}>
            <h3>Stack Actions</h3>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '10px' }}>
              <button onClick={onBack} className="btn" style={{ backgroundColor: 'var(--color-primary)', color: 'var(--text-on-primary)' }}>
                Back
              </button>
              {/* Show Edit button only if user is owner, no submissions, and within 1 hour */}
              {currentUser?.id === ownerId && (!stack.users || stack.users.length === 0) && (() => {
                const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
                const createdAt = new Date(stack.created_at);
                return createdAt > oneHourAgo;
              })() && (
                <button onClick={handleEditClick} className="btn" style={{ backgroundColor: '#28a745', color: 'white' }}>
                  Edit
                </button>
              )}
              <button onClick={() => setShowHideConfirm(true)} className="btn" style={{ backgroundColor: 'var(--color-warning)', color: 'var(--text-on-warning)' }}>
                Hide
              </button>
              {/* Hide for User button - admin only, group stacks only */}
              {currentUser?.role === 'ADMIN' && Boolean(groupId) && (
                <button onClick={handleHideForUserClick} className="btn" style={{ backgroundColor: BUTTON_COLORS.MODERATE_BATCH, color: 'white' }}>
                  Hide for User
                </button>
              )}
              {twistEligible && (
                <button
                  onClick={() => { setTwistError(''); setShowTwistDialog(true); }}
                  className="btn"
                  style={{ backgroundColor: '#6f42c1', color: 'white' }}
                >
                  Twist
                </button>
              )}
              {Boolean(groupId) && (
                (currentUser?.role === 'ADMIN') || (!stack?.is_sandboxed && currentUser?.id === ownerId)
              ) && (
                <button onClick={() => setShowSandboxConfirm(true)} className="btn" style={{ backgroundColor: stack?.is_sandboxed ? '#6c757d' : '#fd7e14', color: 'white' }} disabled={sandboxing}>
                  {sandboxing ? (stack?.is_sandboxed ? 'Unsandboxing...' : 'Sandboxing...') : (stack?.is_sandboxed ? 'Unsandbox' : 'Sandbox')}
                </button>
              )}
              {/* Delete button for bot-generated stacks (admin only) */}
              {stack?.bot && currentUser?.role === 'ADMIN' && (!stack?.users || stack.users.length === 0) && (
                <button onClick={() => setShowDeleteConfirm(true)} className="btn" style={{ backgroundColor: BUTTON_COLORS.DELETE, color: 'white' }}>
                  Delete
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      

      {showHideConfirm && (
        <div className="modal-overlay">
          <div className="modal-card">
            <h3 className="modal-title">Are you sure?</h3>
            <p className="modal-text">Hidden stacks will no longer show on My Stacks</p>
            <div className="modal-actions">
              <button onClick={() => setShowHideConfirm(false)} className="btn" disabled={hiding}>
                Cancel
              </button>
              <button onClick={handleHideStack} className="btn" style={{ backgroundColor: '#dc3545', color: 'white' }} disabled={hiding}>
                {hiding ? 'Hiding...' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSandboxConfirm && (
        <div className="modal-overlay">
          <div className="modal-card">
            <h3 className="modal-title">{stack?.is_sandboxed ? 'Unsandbox Stack?' : 'Sandbox Stack?'}</h3>
            <p className="modal-text">
              {stack?.is_sandboxed
                ? 'Unsandbox to allow all users in the group to see and answer the stack.'
                : 'Sandbox a group stack to prevent it from showing up for new users. All users who answered will still be able to see the stack.'}
            </p>
            <div className="modal-actions">
              <button onClick={() => setShowSandboxConfirm(false)} className="btn" disabled={sandboxing}>
                Cancel
              </button>
              <button onClick={toggleSandbox} className="btn" style={{ backgroundColor: stack?.is_sandboxed ? '#6c757d' : '#fd7e14', color: 'white' }} disabled={sandboxing}>
                {sandboxing ? (stack?.is_sandboxed ? 'Unsandboxing...' : 'Sandboxing...') : (stack?.is_sandboxed ? 'Unsandbox' : 'Sandbox')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="modal-card">
            <h3 className="modal-title">Delete Stack?</h3>
            <p className="modal-text">
              This will permanently delete the stack. This action cannot be undone.
            </p>
            <div className="modal-actions">
              <button onClick={() => setShowDeleteConfirm(false)} className="btn" disabled={deleting}>
                Cancel
              </button>
              <button onClick={handleDeleteStack} className="btn" style={{ backgroundColor: BUTTON_COLORS.DELETE, color: 'white' }} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showTwistDialog && (
        <div className="modal-overlay">
          <div className="modal-card" style={{ maxWidth: '520px' }}>
            <h3 className="modal-title">Twist Stack</h3>
            <p className="modal-text">
              Add your own twist to a stack, and see how answers change! All users who answered the stack will receive the new twisted stack to re-answer. You may only twist a stack once so make it count!
            </p>
            {/* Read-only base name */}
            <div style={{ marginTop: '8px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: 'var(--color-text-muted)' }}>Stack Name</label>
              <input type="text" readOnly value={stack?.name || ''} style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--color-border)', borderRadius: '4px', fontSize: '14px', background: 'var(--color-surface)', color: 'var(--color-text)' }} />
            </div>
            {/* Read-only ordered positions using current user's answer order with original colors */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '8px', marginTop: '10px' }}>
              {(() => {
                const me = (stack?.users || []).find(u => u.user_id === currentUser?.id);
                const myPositions = me ? (me.positions || []) : [];
                const positionsToShow = myPositions.length ? myPositions : (stack?.positions || []);
                return positionsToShow.map((position, idx) => {
                  // Handle both string and object formats
                  const label = typeof position === 'string' ? position : position?.name || '';
                  const colorIndex = getColorIndexForLabel((stack?.positions || []), label, idx);
                  return (
                    <input
                      key={`twist-pos-${idx}`}
                      type="text"
                      readOnly
                      value={label}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid transparent',
                        borderRadius: '6px',
                        fontWeight: 700,
                        fontSize: '14px',
                        color: STACK_TEXT_COLORS[colorIndex],
                        backgroundColor: STACK_COLORS[colorIndex],
                      }}
                    />
                  );
                });
              })()}
            </div>
            {/* Twist input */}
            <div style={{ marginTop: '12px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: 800, color: '#6f42c1' }}>Twist</label>
              <input
                type="text"
                ref={twistInputRef}
                onFocus={onMobileFocus}
                value={twistText}
                onChange={(e) => setTwistText(e.target.value.slice(0, 48))}
                placeholder="something that might make a person think twice about their order"
                maxLength={48}
                style={{ width: '100%', padding: '8px', border: '1px solid var(--color-border)', borderRadius: '4px', fontSize: '14px' }}
              />
              <CharacterCounter current={twistText.length} max={48} style={{ marginTop: '4px' }} />
            </div>
            {twistError && (
              <div style={{ color: 'var(--color-danger)', marginTop: '8px' }}>{twistError}</div>
            )}
            <div className="modal-actions" style={{ marginTop: '12px' }}>
              <button onClick={() => { setShowTwistDialog(false); setTwistError(''); }} className="btn" disabled={twisting}>
                Cancel
              </button>
              <button onClick={handleCreateTwist} className="btn" style={{ backgroundColor: '#6f42c1', color: 'white' }} disabled={twisting || twistText.trim().length === 0}>
                {twisting ? 'Creating…' : 'Create Twisted Stack'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Confirmation Dialog */}
      {showEditConfirm && (
        <div className="modal-overlay">
          <div className="modal-card">
            <h3 className="modal-title">Edit Stack?</h3>
            <p className="modal-text">You can only edit a stack within one hour of posting, and only if no one has responded.</p>
            <div className="modal-actions">
              <button onClick={() => setShowEditConfirm(false)} className="btn">
                Cancel
              </button>
              <button onClick={() => { setShowEditConfirm(false); setShowEditDialog(true); }} className="btn" style={{ backgroundColor: '#28a745', color: 'white' }}>
                Proceed
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Stack Dialog */}
      {showEditDialog && (
        <div className="modal-overlay">
          <div className="modal-card" style={{ maxWidth: '500px', width: '90%' }}>
            <h3 className="modal-title">Edit {stack?.audience?.type === 'friend' ? 'Friend' : 'Group'} Stack</h3>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: 'var(--color-text)' }}>
                Stack Name (optional)
              </label>
              <input
                type="text"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                placeholder="Enter stack name"
                maxLength={32}
                style={{ width: '100%', padding: '8px', border: '1px solid var(--color-border)', borderRadius: '4px', fontSize: '14px' }}
              />
              <CharacterCounter current={editForm.name.length} max={48} />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: 'var(--color-text)' }}>
                Positions (6 required)
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '10px' }}>
                {editForm.positions.map((position, index) => (
                  <div key={index}>
                    <input
                      type="text"
                      value={position}
                      onChange={(e) => {
                        const newPositions = [...editForm.positions];
                        newPositions[index] = e.target.value;
                        setEditForm({ ...editForm, positions: newPositions });
                      }}
                      placeholder={`Position ${index + 1}`}
                      maxLength={32}
                      style={getPositionInputStyles(position, STACK_TEXT_COLORS[index], STACK_COLORS[index])}
                    />
                    <CharacterCounter current={position.length} max={48} />
                  </div>
                ))}
              </div>
            </div>

            <div className="modal-actions">
              <button onClick={() => setShowEditDialog(false)} className="btn" disabled={editing}>
                Cancel
              </button>
              <button 
                onClick={handleEditStack} 
                className="btn" 
                style={{ backgroundColor: '#28a745', color: 'white' }} 
                disabled={editing || editForm.positions.some(p => !String(p || '').trim())}
              >
                {editing ? 'Editing...' : 'Edit Stack'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hide for User Dialog */}
      {showHideForUserDialog && (
        <div className="modal-overlay">
          <div className="modal-card" style={{ maxWidth: '500px', width: '90%' }}>
            <h3 className="modal-title">Hide Stack for Users</h3>
            <p className="modal-text">
              Select users who haven't answered this stack to hide it from them.
            </p>
            
            <div style={{ maxHeight: '300px', overflowY: 'auto', marginTop: '16px' }}>
              {groupMembers.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'var(--color-text-muted)', padding: '20px' }}>
                  No users available to hide from
                </div>
              ) : (
                groupMembers.map((member) => (
                  <div key={member.user_id} style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '12px', 
                    padding: '12px', 
                    border: '1px solid var(--color-border)', 
                    borderRadius: '8px', 
                    marginBottom: '8px',
                    backgroundColor: selectedUserIds.includes(member.user_id) ? 'var(--color-primary-light)' : 'var(--color-surface)'
                  }}>
                    <input
                      type="checkbox"
                      checked={selectedUserIds.includes(member.user_id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedUserIds([...selectedUserIds, member.user_id]);
                        } else {
                          setSelectedUserIds(selectedUserIds.filter(id => id !== member.user_id));
                        }
                      }}
                      style={{ margin: 0 }}
                    />
                    <img 
                      src={getDiceBearUserAvatar(member.user.username, member.user.avatar_id)} 
                      alt={`${member.user.username} avatar`} 
                      style={{ width: '32px', height: '32px', borderRadius: '50%' }} 
                    />
                    <span style={{ fontWeight: 600, color: 'var(--color-text)' }}>{member.user.username}</span>
                  </div>
                ))
              )}
            </div>

            <div className="modal-actions" style={{ marginTop: '16px' }}>
              <button 
                onClick={() => { setShowHideForUserDialog(false); setSelectedUserIds([]); }} 
                className="btn" 
                style={{ backgroundColor: BUTTON_COLORS.CANCEL, color: 'white' }}
                disabled={hidingForUsers}
              >
                Cancel
              </button>
              <button 
                onClick={handleHideForUsers} 
                className="btn" 
                style={{ backgroundColor: BUTTON_COLORS.SUCCESS, color: 'white' }} 
                disabled={hidingForUsers || selectedUserIds.length === 0}
              >
                {hidingForUsers ? 'Hiding...' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default StackProfile;


